import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const WARN_SECONDS = 30;
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

export const SessionTimeout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [timeoutMin, setTimeoutMin] = useState(0);
  const [warning, setWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARN_SECONDS);
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);
  const warningRef = useRef(false);

  useEffect(() => {
    if (!user) { setTimeoutMin(0); return; }
    let active = true;
    api.get("/settings/session")
      .then(({ data }) => { if (active) setTimeoutMin(Number(data.timeout_minutes) || 0); })
      .catch(() => {});
    return () => { active = false; };
  }, [user]);

  const doLogout = useCallback(async () => {
    warningRef.current = false;
    setWarning(false);
    clearTimeout(idleTimer.current);
    clearInterval(countdownTimer.current);
    await logout();
    navigate("/auth");
  }, [logout, navigate]);

  const startCountdown = useCallback(() => {
    warningRef.current = true;
    setCountdown(WARN_SECONDS);
    setWarning(true);
    countdownTimer.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(countdownTimer.current); doLogout(); return 0; }
        return c - 1;
      });
    }, 1000);
  }, [doLogout]);

  const resetIdle = useCallback(() => {
    if (warningRef.current) return;
    clearTimeout(idleTimer.current);
    if (!user || timeoutMin <= 0) return;
    const ms = Math.max(1000, timeoutMin * 60 * 1000 - WARN_SECONDS * 1000);
    idleTimer.current = setTimeout(startCountdown, ms);
  }, [user, timeoutMin, startCountdown]);

  useEffect(() => {
    if (!user || timeoutMin <= 0) {
      clearTimeout(idleTimer.current);
      clearInterval(countdownTimer.current);
      warningRef.current = false;
      setWarning(false);
      return;
    }
    resetIdle();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetIdle));
      clearTimeout(idleTimer.current);
      clearInterval(countdownTimer.current);
    };
  }, [user, timeoutMin, resetIdle]);

  const staySignedIn = () => {
    warningRef.current = false;
    setWarning(false);
    clearInterval(countdownTimer.current);
    resetIdle();
  };

  return (
    <Dialog open={warning} onOpenChange={(v) => { if (!v) staySignedIn(); }}>
      <DialogContent className="max-w-sm" data-testid="session-timeout-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
            <ShieldAlert className="h-6 w-6 text-orange-500" />
          </div>
          <DialogTitle className="text-center font-display text-2xl text-brand-navy">Are you still there?</DialogTitle>
          <DialogDescription className="text-center">
            For your security, you'll be signed out in{" "}
            <span className="font-semibold text-brand-navy" data-testid="session-countdown">{countdown}</span> second{countdown === 1 ? "" : "s"} due to inactivity.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={staySignedIn} className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white" data-testid="session-stay">Stay signed in</Button>
          <Button onClick={doLogout} variant="outline" className="w-full rounded-full" data-testid="session-logout-now">Sign out now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
